import { RootState } from '@state/types';
import { extractActionNameWithoutSuffix } from '../common/utils';
import { getWorkload } from './actions';
import { createSelector } from 'reselect';
import { CourseType, TaskState, WorkloadTask } from 'shovel-lib/types';
import { SidebarItemName, SidebarItemType } from './types';

export type CourseEstimateStatus = {
  courseId: number;
  courseName: string;
  colorHex: string;
  unestimatedCount: number;
};

const isUnestimatedTask = (task: WorkloadTask) => {
  if (task.deleted || task.state === TaskState.COMPLETED || !task.courseId) return false;
  if (task.reading) {
    const pages = (task.pageRanges || []).reduce((sum, range) => sum + (range.pages || 0), 0);
    return pages <= 0;
  }
  return !(task.timeNeed || task.timeSpent);
};

/** Unestimated incomplete tasks grouped by course. Shared by the toolbar badge (and should stay in lockstep with import Estimate rows). */
export const getUnestimatedByCourse = createSelector(
  [(state: RootState) => state.workload.tasks, (state: RootState) => state.calendar.courses],
  (tasks, courses): CourseEstimateStatus[] =>
    courses.map(course => ({
      courseId: course.id,
      courseName: course.name,
      colorHex: course.colorHex,
      unestimatedCount: tasks.filter(task => task.courseId === course.id && isUnestimatedTask(task)).length
    }))
);

export type CourseCategorizationStatus = {
  courseId: number;
  courseName: string;
  colorHex: string;
  uncategorizedCount: number;
};

const isUncategorizedTask = (task: WorkloadTask) =>
  !task.deleted && task.state !== TaskState.COMPLETED && !!task.courseId && !task.taskCategory;

/** Uncategorized tasks grouped by course. Shared by every "Auto-categorize" entry point (task list, Pile, Estimator). */
export const getUncategorizedByCourse = createSelector(
  [(state: RootState) => state.workload.tasks, (state: RootState) => state.calendar.courses],
  (tasks, courses): CourseCategorizationStatus[] =>
    courses.map(course => ({
      courseId: course.id,
      courseName: course.name,
      colorHex: course.colorHex,
      uncategorizedCount: tasks.filter(task => task.courseId === course.id && isUncategorizedTask(task)).length
    }))
);

export type SidebarTaskCounts = {
  byCourse: Record<string, number>;
  byCategory: Record<string, number>;
  noCategoryByCourse: Record<string, number>;
};

/**
 * Open tasks (not completed, not in Trash) for the Task List sidebar's rows: each course or activity, each category,
 * and each course's "No category" - the tasks a click on the row shows (Petr).
 */
export const getSidebarTaskCounts = createSelector(
  [(state: RootState) => state.workload.tasks, (state: RootState) => state.workload.newWorkloadTasks],
  (tasks, newTasks): SidebarTaskCounts => {
    const counts: SidebarTaskCounts = { byCourse: {}, byCategory: {}, noCategoryByCourse: {} };
    const add = (bucket: Record<string, number>, key: string | number) => (bucket[key] = (bucket[key] || 0) + 1);
    // Tasks just added from a group's own add row wait apart until the view changes; they count at once.
    const counted = new Set<number>();
    [...tasks, ...Object.values(newTasks || {}).flat()].forEach(task => {
      if (counted.has(task.id)) return;
      counted.add(task.id);
      if (task.deleted || task.state === TaskState.COMPLETED || !task.courseId) return;
      add(counts.byCourse, task.courseId);
      if (task.taskCategory?.id) add(counts.byCategory, task.taskCategory.id);
      else add(counts.noCategoryByCourse, task.courseId);
    });
    return counts;
  }
);

export const getCategorizingCourseIds = (state: RootState) => state.workload.categorizingCourseIds;

export const getPendingEstimateReminders = (state: RootState) => state.workload.pendingEstimateReminders;

export const isWorkloadLoading = ({ common }: RootState) =>
  common.loaders[extractActionNameWithoutSuffix(getWorkload.request)];

export const getWorkloadState = createSelector(
  [
    ({ workload }: RootState) => workload.tasks,
    state => state.workload.selectedItemId,
    state => state.workload.selectedItemType,
    state => state.workload.selectedItemCourseId
  ],
  (tasks, selectedItemId, selectedItemType, selectedItemCourseId) => {
    const sectionFilter = t =>
      !t.deleted &&
      selectedItemId !== SidebarItemName.TRASH &&
      (!selectedItemId || // all tasks section selected
      (selectedItemType === SidebarItemType.SECTION &&
        selectedItemId === SidebarItemName.COURSE &&
        !!t.courseId &&
        t.courseType === CourseType.COURSE) || // all courses selected
      (selectedItemType === SidebarItemType.SECTION &&
        selectedItemId === SidebarItemName.ACTIVITY &&
        !!t.courseId &&
        t.courseType === CourseType.ACTIVITY) || // all activities selected
      (selectedItemType === SidebarItemType.SECTION && selectedItemId === SidebarItemName.RANDOM && !t.courseId) || // random (no course) section
      ((selectedItemType === SidebarItemType.COURSE || selectedItemType === SidebarItemType.ACTIVITY) &&
        t.courseId === selectedItemId) || // a course or activity is selected
        (selectedItemType === SidebarItemType.TASK_CATEGORY &&
          filterByTaskCategory(t, selectedItemId, selectedItemCourseId))); // task category is selected

    return {
      tasks: tasks.filter(t =>
        selectedItemId === SidebarItemName.TRASH ? t.deleted : t.state !== TaskState.COMPLETED && sectionFilter(t)
      ),
      completedTasks: tasks.filter(t => t.state === TaskState.COMPLETED && sectionFilter(t))
    };
  }
);

const filterByTaskCategory = (task: any, selectedItemId: string | number, selectedItemCourseId?: string | number) => {
  if (selectedItemId.toString().includes('uncategorized')) {
    return !task.taskCategory && task.courseId === selectedItemCourseId;
  }
  return task.taskCategory?.id === selectedItemId;
};

export const getNewWorkloadTasks = ({ workload }: RootState) => workload.newWorkloadTasks;

export const getAddedTaskIds = ({ workload }: RootState) => workload.addedTaskIds;

export const getSelectedCourseId = ({ workload }: RootState) =>
  workload.selectedItemType === SidebarItemType.COURSE || workload.selectedItemType === SidebarItemType.ACTIVITY
    ? (workload.selectedItemId as number)
    : undefined;

export const getSelectedItemInfo = ({ workload }: RootState) => {
  const {
    selectedItemId,
    selectedItemType,
    selectedItemColor,
    selectedItemName,
    loading,
    expandedItemType,
    expandedItemId,
    selectedItemCourseId
  } = workload;
  return {
    loading,
    selectedItemId,
    selectedItemType,
    selectedItemName,
    selectedItemColor,
    expandedItemType,
    expandedItemId,
    selectedItemCourseId
  };
};
